import { useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { GenericTable } from "../../../Shared/components/Table/GenericTable";
import { TableActionButtons } from "../../../Shared/components/Table/TableActionButtons";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { IDriver } from "../models/Driver";
import { DriverService } from "../services/DriverService";
import { DriverForm } from "./DriverForm";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { useNavigate } from "react-router-dom";
import { FileImportForm } from "../../../Shared/components/FileImportForm";
import {
  ImportFileActionType,
  ImportFileLabelType,
} from "../../../Shared/enums/importFileActionType";
import { ConfirmDialog } from "../../../Shared/components/ConfirmDialog";
import { AuthService } from "../../Auth/services/AuthService";

export const Drivers = () => {
  const navigate = useNavigate();
  const message = "Loading Driver Data, Please Wait";
  const isAllowed = AuthService.isManagementAllowed();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedDrivers, setSelectedDrivers] = useState<IDriver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<IDriver>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const findDrivers = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: globalFilter,
      };
      setIsLoading(true);
      const response = await DriverService.getAll(pageOptions);
      setIsLoading(false);
      return response;
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: [
      "drivers",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: findDrivers,
    placeholderData: (previous) => previous?.data,
  });

  const debouncedSetFilter = useMemo(
    () =>
      debounce((val: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGlobalFilter(val);
      }, 200),
    []
  );

  const onPaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  const getPageCount = () => {
    const total = query.data?.meta?.itemCount ?? 0;
    return Math.ceil(total / pagination.pageSize);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onAddAction = () => setIsModalOpen(!isModalOpen);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const onImportAction = () => setIsImportModalOpen(!isModalOpen);
  const toggleImportModal = (success: boolean) => {
    setIsImportModalOpen(!isImportModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const onRowEditAction = (row: IDriver) => {
    setSelectedDriver(row);
    onAddAction();
  };

  const onRowViewAction = (row: IDriver) => {
    setSelectedDriver(row);
    navigate(`/services/vehicles/drivers/details/?driver=${row.id}`);
  };

  const onRowDeleteAction = (row: IDriver) => {
    setSelectedDriver(row);
    setIsDialogOpen(true);
  };

  const deleteAction = async () => {
    const approvals =
      selectedDrivers?.length > 0
        ? selectedDrivers.map((x) => x.id!)
        : [selectedDriver?.id ?? ""];
    try {
      const response = await DriverService.delete(approvals);
      if (response) {
        query.refetch();
      }
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    }
  };

  const onExportAction = () => {
    UtilService.exportToExcel(query?.data?.data, "Driver");
  };

  const tableAction = !isAllowed ? (
    <TableActionButtons
      onAdd={onAddAction}
      onImport={onImportAction}
      onExport={onExportAction}
    />
  ) : (
    <TableActionButtons onExport={onExportAction} />
  );

  const columns = useMemo<ColumnDef<IDriver>[]>(
    () => [
      {
        accessorKey: "firstName",
        header: "First Name",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone Number",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "email",
        header: "Email Address",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <div className="">
      <div className="relative">
        {isModalOpen && (
          <DriverForm onClose={toggleModal} driver={selectedDriver} />
        )}

        {isImportModalOpen && (
          <FileImportForm
            onClose={toggleImportModal}
            actionType={ImportFileActionType.DRIVERS}
            actionLabel={ImportFileLabelType.DRIVERS}
          />
        )}

        <ConfirmDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={deleteAction}
          itemName={`${selectedDriver?.firstName}  ${selectedDriver?.lastName}`}
        />
        <GenericTable
          isLoading={isLoading}
          data={query?.data?.data ?? []}
          meta={query?.data?.meta ?? 0}
          columns={columns}
          globalFilter={globalFilter}
          onGlobalFilterChange={debouncedSetFilter}
          pageCount={getPageCount()}
          onPaginationChange={onPaginationChange}
          onRowSelectionChange={setSelectedDrivers}
          {...(!isAllowed && { onEdit: onRowEditAction })}
          onMore={onRowViewAction}
          onDelete={onRowDeleteAction}
          actionButtons={tableAction}
        />
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </div>
  );
};
